/**
 * AdminPage — Super Admin control panel.
 *
 * Gated to elevated platform roles (isElevatedRole, lib/roles.ts).
 * Tabs: Users (Core accounts plus their Teamwork internal member, O41),
 * Workspaces, Integrations, Audit Log.
 *
 * All data is read-only except role promotion (platform_owner only).
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck, Users, Building2, ScrollText, Search, ChevronLeft, ChevronRight, UserPlus, Check, ArrowUpDown, ArrowUp, ArrowDown, Plug, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isElevatedRole } from '@/lib/roles';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import {
  fetchAdminUsers,
  fetchAdminAuditLog,
  setUserPlatformRole,
  createAdminInvite,
  patchTeamworkMember,
} from '@/services/authApi';
import { AgencyGoogleAdsIntegrationCard } from '@/components/admin/AgencyGoogleAdsIntegrationCard';
import { IntegrationProviderGrid } from '@/components/admin/IntegrationProviderGrid';
import { InternalMemberDialog } from '@/components/admin/InternalMemberDialog';
import { WorkspacesTable } from '@/components/admin/WorkspacesTable';
import type {
  AdminUser, AdminAuditEvent, AdminUsersResponse, InternalMember, TeamworkMemberPatch,
} from '@/services/authApi';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function platformRoleBadge(role: string) {
  switch (role) {
    case 'platform_owner':
      return <Badge variant="outline" className="bg-coral/10 text-coral-dark border-coral/20">Platform Owner</Badge>;
    case 'super_admin':
      return <Badge variant="outline" className="bg-teal-light text-teal-dark border-teal/20">Super Admin</Badge>;
    // Without this case an expert rendered as "User" — the admin screen
    // actively misreporting a role that carries cross-tenant data access.
    case 'expert':
      return <Badge variant="outline" className="bg-teal-light/50 text-teal-dark border-teal/30">Internal Expert</Badge>;
    default:
      return <Badge variant="outline" className="bg-muted text-muted-foreground">User</Badge>;
  }
}

/**
 * Cross-tenant roles reach every workspace by role, not by membership, so
 * their membership count is legitimately zero — `GET /auth/workspaces` returns
 * all workspaces for them without consulting `workspace_members` at all.
 *
 * Rendering that bare zero under a column headed "Workspaces" reads as "this
 * person has no access", which is the opposite of the truth, and it got filed
 * as a bug off the back of the 23 Jul demo. Show what actually governs access.
 */
function membershipCell(u: { platform_role: string; workspace_count: number }) {
  if (CROSS_TENANT_ROLES.has(u.platform_role)) {
    return (
      <span
        className="text-muted-foreground"
        title={`Reaches every workspace by role. Explicit memberships: ${u.workspace_count}.`}
      >
        All
      </span>
    );
  }
  return <span className="tabular-nums">{u.workspace_count}</span>;
}

/** Mirrors CROSS_TENANT_PLATFORM_ROLES in the backend permission service. */
const CROSS_TENANT_ROLES = new Set(['platform_owner', 'super_admin', 'expert']);

function statusBadge(isActive: boolean) {
  return isActive
    ? <Badge variant="outline" className="bg-teal-light text-teal-dark border-teal/20">Active</Badge>
    : <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20">Inactive</Badge>;
}

const USERS_KEY = ['admin', 'users'];
/** The planner's assignee list (usePlanner.useAssignees): inactive members drop out. */
const ASSIGNEES_KEY = ['planner-assignees'];

/** The users answer with one member's fields changed (the optimistic update). */
function withMemberChanges(data: AdminUsersResponse, personId: string, body: TeamworkMemberPatch) {
  return {
    ...data,
    users: data.users.map((u) =>
      u.internal_member?.teamwork_person_id === personId
        ? { ...u, internal_member: { ...u.internal_member, ...body } }
        : u),
  };
}

/**
 * The Teamwork person behind a row (O41): an Active switch (hidden from new
 * Agile Plan picks when off, O38), "Internal" or "Former" (worded apart from the
 * account Status badge, O45), "Not in Teamwork" when Teamwork no longer lists
 * them, and the edit button. "—" when no person matches.
 */
function internalMemberCell(
  im: InternalMember | null,
  onToggle: (im: InternalMember, active: boolean) => void,
  onEdit: (im: InternalMember) => void,
) {
  if (!im) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Switch
        checked={im.active}
        aria-label={`Active: ${im.name}`}
        onCheckedChange={(next) => onToggle(im, next)}
      />
      <Badge variant="outline" className={im.active ? 'bg-teal-light text-teal-dark border-teal/20' : 'bg-muted text-muted-foreground'}>
        {im.active ? 'Internal' : 'Former'}
      </Badge>
      {im.not_in_teamwork && <Badge variant="outline">Not in Teamwork</Badge>}
      {im.display_name_override && (
        <span className="text-xs text-muted-foreground" title="Shown in the Agile Plan as">“{im.display_name_override}”</span>
      )}
      <Button variant="ghost" size="sm" className="h-7 px-2" aria-label={`Edit ${im.name}`} onClick={() => onEdit(im)}>
        <Pencil className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDateTime(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function SortableHead({ label, sortKey: key, currentKey, dir, onSort }: {
  label: string;
  sortKey: string;
  currentKey: string;
  dir: 'asc' | 'desc';
  onSort: (key: string) => void;
}) {
  const isActive = currentKey === key;
  return (
    <TableHead
      className="cursor-pointer select-none hover:text-foreground whitespace-nowrap"
      onClick={() => onSort(key)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive ? (
          dir === 'asc'
            ? <ArrowUp className="h-3 w-3 text-teal" />
            : <ArrowDown className="h-3 w-3 text-teal" />
        ) : (
          <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />
        )}
      </span>
    </TableHead>
  );
}

// ---------------------------------------------------------------------------
// Users Tab
// ---------------------------------------------------------------------------

function UsersTab({ isPlatformOwner }: { isPlatformOwner: boolean }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showFilter, setShowFilter] = useState<'all' | 'core' | 'teamwork_only'>('all');
  const [internalFilter, setInternalFilter] = useState<'' | 'active' | 'inactive' | 'none'>('');
  const [editing, setEditing] = useState<InternalMember | null>(null);
  const [sortKey, setSortKey] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [inviteExpiry, setInviteExpiry] = useState<string | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: [...USERS_KEY, page, search, statusFilter, roleFilter, showFilter, internalFilter],
    queryFn: () => fetchAdminUsers({
      page,
      search: search || undefined,
      status: statusFilter || undefined,
      platform_role: roleFilter || undefined,
      show: showFilter === 'all' ? undefined : showFilter,
      internal: internalFilter || undefined,
    }),
  });

  // Keyed on the Teamwork person, not the Core account (O42). Optimistic, so the
  // switch moves at once; an error puts every cached page back.
  const memberMutation = useMutation({
    mutationFn: ({ personId, body }: { personId: string; body: TeamworkMemberPatch }) =>
      patchTeamworkMember(personId, body),
    onMutate: async ({ personId, body }) => {
      await queryClient.cancelQueries({ queryKey: USERS_KEY });
      const previous = queryClient.getQueriesData<AdminUsersResponse>({ queryKey: USERS_KEY });
      queryClient.setQueriesData<AdminUsersResponse>(
        { queryKey: USERS_KEY },
        (old) => (old ? withMemberChanges(old, personId, body) : old),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      context?.previous.forEach(([key, value]) => queryClient.setQueryData(key, value));
      toast.error("Couldn't update the internal member");
    },
    onSuccess: () => {
      toast.success('Internal member updated');
      void queryClient.invalidateQueries({ queryKey: USERS_KEY });
      void queryClient.invalidateQueries({ queryKey: ASSIGNEES_KEY });
    },
  });

  const toggleMember = (im: InternalMember, active: boolean) =>
    memberMutation.mutate({ personId: im.teamwork_person_id, body: { active } });

  const roleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      setUserPlatformRole(userId, role),
    onSuccess: (result) => {
      toast.success(`Role updated: ${result.old_role} → ${result.new_role}`);
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to update role';
      toast.error(msg);
    },
  });

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 1;

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  }

  function handleRoleChange(user: AdminUser, newRole: string) {
    if (!user.id || newRole === user.platform_role) return;
    roleMutation.mutate({ userId: user.id, role: newRole });
  }

  function handleSort(key: string) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const sortedUsers = data ? [...data.users].sort((a, b) => {
    let aVal: string | number | boolean = '';
    let bVal: string | number | boolean = '';
    switch (sortKey) {
      case 'email': aVal = a.email?.toLowerCase() ?? ''; bVal = b.email?.toLowerCase() ?? ''; break;
      case 'name': aVal = (a.display_name || a.full_name || '').toLowerCase(); bVal = (b.display_name || b.full_name || '').toLowerCase(); break;
      case 'platform_role': aVal = a.platform_role ?? ''; bVal = b.platform_role ?? ''; break;
      case 'workspace_count': aVal = a.workspace_count; bVal = b.workspace_count; break;
      case 'is_active': aVal = a.is_active ? 1 : 0; bVal = b.is_active ? 1 : 0; break;
      case 'created_at': aVal = a.created_at ?? ''; bVal = b.created_at ?? ''; break;
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  }) : [];

  async function handleGenerateInvite() {
    setInviteLoading(true);
    try {
      const result = await createAdminInvite('super_admin', inviteEmail || undefined);
      setInviteLink(result.invite_link);
      setInviteExpiry(result.expires_at);
      if (result.email_sent) {
        toast.success(`Invite email sent to ${inviteEmail}`);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to generate invite';
      toast.error(msg);
    } finally {
      setInviteLoading(false);
    }
  }

  async function handleCopy() {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <>
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-teal" />
              All Users
            </CardTitle>
            <CardDescription>
              {data ? `${data.total} total (Core accounts and Teamwork-only people)` : 'Loading...'}
            </CardDescription>
            {/* The ladder, stated where it is edited. Platform roles are
                AGENCY-side and global; a client's own admin is a workspace
                role (owner/admin/editor/viewer) set per workspace, which is
                why it is not in this list. */}
            <p className="mt-2 max-w-[68ch] text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Platform roles</span>{' '}
              are agency-wide:{' '}
              <span className="font-medium text-coral-dark">Platform Owner</span>{' '}
              (grants roles) ›{' '}
              <span className="font-medium text-teal-dark">Super Admin</span>{' '}
              (full admin, any tenant) ›{' '}
              <span className="font-medium text-teal-dark">Internal Expert</span>{' '}
              (cross-tenant read, no admin) ›{' '}
              <span className="font-medium">User</span>. Client-side admins are
              set per workspace instead, so their access never spans other
              clients.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isPlatformOwner && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setInviteOpen(true); setInviteLink(null); setInviteExpiry(null); }}
                className="gap-2 text-teal-dark border-teal/30 hover:bg-teal-light"
              >
                <UserPlus className="h-4 w-4" />
                Invite Admin
              </Button>
            )}
            <form onSubmit={handleSearch} className="flex gap-2">
              <Input
                placeholder="Search email or name..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-60"
              />
              <Button type="submit" variant="outline" size="sm">
                <Search className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filter bar */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <Select value={statusFilter || 'all'} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="h-8 w-36 text-xs">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>

          <Select value={roleFilter || 'all'} onValueChange={(v) => { setRoleFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="h-8 w-40 text-xs">
              <SelectValue placeholder="All roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="platform_owner">Platform Owner</SelectItem>
              <SelectItem value="super_admin">Super Admin</SelectItem>
              <SelectItem value="user">User</SelectItem>
            </SelectContent>
          </Select>

          <Select value={showFilter} onValueChange={(v) => { setShowFilter(v as typeof showFilter); setPage(1); }}>
            <SelectTrigger className="h-8 w-40 text-xs" aria-label="Show">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="core">Core accounts</SelectItem>
              <SelectItem value="teamwork_only">Teamwork only</SelectItem>
            </SelectContent>
          </Select>

          <Select value={internalFilter || 'any'} onValueChange={(v) => { setInternalFilter(v === 'any' ? '' : v as typeof internalFilter); setPage(1); }}>
            <SelectTrigger className="h-8 w-40 text-xs" aria-label="Internal member">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              <SelectItem value="active">Internal</SelectItem>
              <SelectItem value="inactive">Former</SelectItem>
              <SelectItem value="none">None</SelectItem>
            </SelectContent>
          </Select>

          {(statusFilter || roleFilter || showFilter !== 'all' || internalFilter) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs"
              onClick={() => {
                setStatusFilter(''); setRoleFilter(''); setShowFilter('all'); setInternalFilter(''); setPage(1);
              }}
            >
              Clear filters
            </Button>
          )}
        </div>
        {(statusFilter || roleFilter) && showFilter !== 'core' && (
          <p className="text-xs text-muted-foreground mb-3">
            Teamwork-only rows are hidden while a Status or Role filter is set (those are Core account settings).
          </p>
        )}
        {data && data.internal_member_available === false && (
          <p className="text-xs text-muted-foreground mb-3">
            Internal member data is unavailable on this environment (the Teamwork members migration isn&apos;t applied).
          </p>
        )}
        {isLoading && <p className="text-sm text-muted-foreground py-4">Loading users...</p>}
        {isError && <p className="text-sm text-destructive py-4">Failed to load users.</p>}
        {data && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead label="Email" sortKey="email" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableHead label="Name" sortKey="name" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableHead label="Platform Role" sortKey="platform_role" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableHead label="Memberships" sortKey="workspace_count" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableHead label="Status" sortKey="is_active" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                  <TableHead className="whitespace-nowrap">Internal member</TableHead>
                  <TableHead>Department</TableHead>
                  <SortableHead label="Joined" sortKey="created_at" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                  {isPlatformOwner && <TableHead>Change Role</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isPlatformOwner ? 9 : 8} className="text-center text-muted-foreground py-8">
                      No users found.
                    </TableCell>
                  </TableRow>
                )}
                {sortedUsers.map((u) => {
                  const core = u.row_type !== 'teamwork_only';
                  return (
                    <TableRow key={u.id ?? `tw-${u.internal_member?.teamwork_person_id}`}>
                      <TableCell className="font-mono text-xs">{u.email ?? '—'}</TableCell>
                      <TableCell>
                        {u.display_name || u.full_name || '—'}
                        {!core && (
                          <Badge variant="outline" className="ml-2 bg-muted text-muted-foreground">Teamwork only</Badge>
                        )}
                      </TableCell>
                      <TableCell>{core ? platformRoleBadge(u.platform_role ?? 'user') : '—'}</TableCell>
                      <TableCell className="text-center">
                        {core ? membershipCell({ platform_role: u.platform_role ?? 'user', workspace_count: u.workspace_count }) : '—'}
                      </TableCell>
                      <TableCell>{core ? statusBadge(!!u.is_active) : '—'}</TableCell>
                      <TableCell data-testid="internal-member-cell">
                        {internalMemberCell(u.internal_member, toggleMember, setEditing)}
                      </TableCell>
                      <TableCell className="text-sm">{u.internal_member?.department ?? '—'}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(u.created_at)}</TableCell>
                      {isPlatformOwner && (
                        <TableCell>
                          {core && (
                            <Select
                              value={u.platform_role ?? 'user'}
                              onValueChange={(val) => handleRoleChange(u, val)}
                              disabled={u.platform_role === 'platform_owner' || roleMutation.isPending}
                            >
                              <SelectTrigger className="h-7 w-36 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              {/* Weakest first, so the list reads as the ladder.
                                  Platform Owner is shown but never selectable —
                                  the role that grants roles is not handed out
                                  from the screen that grants roles. */}
                              <SelectContent>
                                <SelectItem value="user">User</SelectItem>
                                <SelectItem value="expert">Internal Expert</SelectItem>
                                <SelectItem value="super_admin">Super Admin</SelectItem>
                                {u.platform_role === 'platform_owner' && (
                                  <SelectItem value="platform_owner" disabled>Platform Owner</SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline" size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>

    {editing && (
      <InternalMemberDialog
        key={editing.teamwork_person_id}
        open
        onOpenChange={(open) => { if (!open) setEditing(null); }}
        member={editing}
        saving={memberMutation.isPending}
        onSave={(changes) => memberMutation.mutate(
          { personId: editing.teamwork_person_id, body: changes },
          { onSuccess: () => setEditing(null) },
        )}
      />
    )}

    <Dialog open={inviteOpen} onOpenChange={(open) => { setInviteOpen(open); if (!open) { setInviteLink(null); setInviteExpiry(null); setInviteEmail(''); } }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-teal" />
            Invite Super Admin
          </DialogTitle>
          <DialogDescription>
            Generate a one-time link valid for 7 days. Share it directly with the person you want to invite.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Role:</span>
            <Badge variant="outline" className="bg-teal-light text-teal-dark border-teal/20">Super Admin</Badge>
          </div>
          <div className="space-y-1">
            <Label className="text-sm">Send invite to (optional)</Label>
            <Input
              type="email"
              placeholder="admin@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              disabled={!!inviteLink}
            />
            <p className="text-xs text-muted-foreground">
              Leave blank to only get a shareable link.
            </p>
          </div>
          {!inviteLink ? (
            <Button
              onClick={handleGenerateInvite}
              disabled={inviteLoading}
              className="w-full bg-teal hover:bg-teal-hover text-white"
            >
              {inviteLoading ? 'Generating...' : 'Generate Link'}
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input value={inviteLink} readOnly className="font-mono text-xs" />
                <Button variant="outline" size="sm" onClick={handleCopy} className="shrink-0 w-20">
                  {copied ? <><Check className="h-4 w-4 text-teal" /> Copied</> : 'Copy'}
                </Button>
              </div>
              {inviteExpiry && (
                <p className="text-xs text-muted-foreground">
                  Expires: {new Date(inviteExpiry).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setInviteLink(null); setInviteExpiry(null); }}
                className="w-full text-xs"
              >
                Generate a new link
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Audit Log Tab
// ---------------------------------------------------------------------------

function AuditLogTab() {
  const [page, setPage] = useState(1);
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const PAGE_SIZE = 100;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'audit-log', page, eventTypeFilter, userIdFilter],
    queryFn: () => fetchAdminAuditLog({
      page,
      page_size: PAGE_SIZE,
      event_type: eventTypeFilter || undefined,
      user_id: userIdFilter || undefined,
    }),
  });

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 1;

  function MetadataCell({ event }: { event: AdminAuditEvent }) {
    const [expanded, setExpanded] = useState(false);
    const hasData = event.metadata && Object.keys(event.metadata).length > 0;
    if (!hasData) return <span className="text-muted-foreground text-xs">—</span>;
    return (
      <button
        onClick={() => setExpanded((x) => !x)}
        className="text-left text-xs text-teal-dark hover:underline"
      >
        {expanded
          ? <pre className="whitespace-pre-wrap text-xs text-foreground bg-muted rounded p-2 max-w-xs">{JSON.stringify(event.metadata, null, 2)}</pre>
          : 'View details'
        }
      </button>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ScrollText className="h-5 w-5 text-teal" />
              Audit Log
            </CardTitle>
            <CardDescription>
              {data ? `${data.total} total events` : 'Loading...'}
            </CardDescription>
          </div>
          {/* Filters */}
          <div className="flex gap-3 items-end">
            <div className="flex flex-col gap-1">
              <Label className="text-xs text-muted-foreground">Event type</Label>
              <Input
                placeholder="e.g. account.deactivated"
                value={eventTypeFilter}
                onChange={(e) => { setEventTypeFilter(e.target.value); setPage(1); }}
                className="w-48 h-8 text-xs"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs text-muted-foreground">User ID</Label>
              <Input
                placeholder="UUID..."
                value={userIdFilter}
                onChange={(e) => { setUserIdFilter(e.target.value); setPage(1); }}
                className="w-48 h-8 text-xs font-mono"
              />
            </div>
            {(eventTypeFilter || userIdFilter) && (
              <Button
                variant="ghost" size="sm"
                onClick={() => { setEventTypeFilter(''); setUserIdFilter(''); setPage(1); }}
                className="h-8 text-xs"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-sm text-muted-foreground py-4">Loading audit log...</p>}
        {isError && <p className="text-sm text-destructive py-4">Failed to load audit log.</p>}
        {data && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.events.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      No events found.
                    </TableCell>
                  </TableRow>
                )}
                {data.events.map((ev) => (
                  <TableRow key={ev.id}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDateTime(ev.created_at)}
                    </TableCell>
                    <TableCell className="text-xs">
                      <div>{ev.user_email || '—'}</div>
                      {ev.user_id && (
                        <div className="font-mono text-muted-foreground text-[10px]">
                          {ev.user_id.slice(0, 8)}…
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs font-mono bg-muted/50">
                        {ev.event_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">
                      {ev.ip_address || '—'}
                    </TableCell>
                    <TableCell>
                      <MetadataCell event={ev} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AdminPage() {
  const { userProfile } = useWorkspaceStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const platformRole = userProfile?.platform_role;
  const isElevated = isElevatedRole(platformRole);
  const isPlatformOwner = platformRole === 'platform_owner';
  const requestedTab = searchParams.get('tab');
  const activeTab = ['users', 'workspaces', 'integrations', 'audit-log'].includes(
    requestedTab ?? '',
  )
    ? requestedTab!
    : 'users';

  const handleTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams.toString());
    if (value === 'users') {
      next.delete('tab');
    } else {
      next.set('tab', value);
    }
    setSearchParams(next, { replace: true });
  };

  if (!isElevated) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <ShieldCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Admin Access Required</h2>
        <p className="text-muted-foreground">
          This page is restricted to Super Admins and Platform Owners.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-teal-light flex items-center justify-center">
          <ShieldCheck className="h-5 w-5 text-teal-dark" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Admin Panel</h1>
          <p className="text-sm text-muted-foreground">
            Platform management — {isPlatformOwner ? 'Platform Owner' : 'Super Admin'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="users" className="gap-2">
            <Users className="h-4 w-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="workspaces" className="gap-2">
            <Building2 className="h-4 w-4" />
            Workspaces
          </TabsTrigger>
          <TabsTrigger value="integrations" className="gap-2">
            <Plug className="h-4 w-4" />
            Integrations
          </TabsTrigger>
          <TabsTrigger value="audit-log" className="gap-2">
            <ScrollText className="h-4 w-4" />
            Audit Log
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-6">
          <UsersTab isPlatformOwner={isPlatformOwner} />
        </TabsContent>

        <TabsContent value="workspaces" className="mt-6">
          <WorkspacesTable />
        </TabsContent>

        <TabsContent value="integrations" className="mt-6 space-y-6">
          <AgencyGoogleAdsIntegrationCard />
          <IntegrationProviderGrid />
        </TabsContent>

        <TabsContent value="audit-log" className="mt-6">
          <AuditLogTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
